import { isValidDeliveryTerms, isValidShipVia, isValidCountryCode } from "../../components/shared/helpers";
import { StringUtils } from "../../components/shared/StringUtils";
import { CheckoutCommonField, CheckoutCommonFields, requiredCheckoutCommonFieldNames } from "../../models/ReceivedOrder";


export const hasRequiredErrors = (checkoutCommonFields: CheckoutCommonField[]) => {

  const fieldsWithError = checkoutCommonFields.filter(x => x.ErrorMessage && x.ErrorMessage.length > 0);
  return fieldsWithError.length > 0;
}

export const validateCheckoutCommonFields = (checkoutCommonFields: CheckoutCommonField[]) => {
  checkoutCommonFields = addMissingCheckoutCommonFields(checkoutCommonFields);

  for (const field of checkoutCommonFields) {
    if (!isValidRequiredField(field)) {
      field.ErrorMessage = "This field is required.";
      continue;
    }

    const maxLenValid = isValidMaxLengthValue(field)
    if (!maxLenValid.isValid) {
      field.ErrorMessage = `The value entered exceeds the maximum allowed length. Maximum length is ${maxLenValid.maxLen} characters.`;
      continue;
    }

    switch (field.FieldName) {
      case CheckoutCommonFields.BuyersEmail:
        if (!StringUtils.IsValidEmail(field.Value)) {
          field.ErrorMessage = "The Email format is not valid.";
        }
        break;
      case CheckoutCommonFields.DeliveryTerms:
        if (!isValidDeliveryTerms(field.Value)) {
          field.ErrorMessage = "The value is not valid. Must be one of: 'Collect', 'Prepaid & Add', 'Third Party'";
        }
        break;
      case CheckoutCommonFields.ShipVia:
        if (!isValidShipVia(field.Value)) {
          field.ErrorMessage = "The value is not valid. Must be one of: 'Best Way Air' or 'Best Way Ground'";
        }
        break;
      case CheckoutCommonFields.ShipToCountry:
      case CheckoutCommonFields.BillToCountry:
        if (!isValidCountryCode(field.Value)) {
          field.ErrorMessage = "The Country Code is not valid. Must be one of: 'US' or 'CA'.";
        }
        break;
      case CheckoutCommonFields.ShipToZipOrPostal:
        const shipToCountry = checkoutCommonFields.find(x => x.FieldName === CheckoutCommonFields.ShipToCountry)

        let message = "The Zip Or Postal Code is not valid.";
        if ((shipToCountry.Value.toUpperCase() === "US"
          && StringUtils.IsValidUSAZipCode(field.Value))
          || (shipToCountry.Value.toUpperCase() === "CA"
            && StringUtils.IsValidCanadaPostalCode(field.Value))) {

          message = "";
        }

        field.ErrorMessage = message;
        break;
      case CheckoutCommonFields.BillToZipOrPostal:
        const billToCountry = checkoutCommonFields.find(x => x.FieldName === CheckoutCommonFields.BillToCountry)

        let billMessage = "The Zip Or Postal Code is not valid.";
        if ((billToCountry.Value.toUpperCase() === "US"
          && StringUtils.IsValidUSAZipCode(field.Value))
          || (billToCountry.Value.toUpperCase() === "CA"
            && StringUtils.IsValidCanadaPostalCode(field.Value))) {

          billMessage = "";
        }

        field.ErrorMessage = billMessage;
        break;
      case CheckoutCommonFields.ShipToStateOrProvince:
      case CheckoutCommonFields.BillToStateOrProvince:
        if (!/^[A-Za-z]{2}$/.test(field.Value)) {
          field.ErrorMessage = "The State Or Province must be exactly 2 alphabetic characters.";
        }
        break;
      case CheckoutCommonFields.CollectAccountNumber:
      case CheckoutCommonFields.CollectCarrier:
        const deliveryTerms = checkoutCommonFields.find(x => x.FieldName === CheckoutCommonFields.DeliveryTerms);
        if (deliveryTerms.Value === 'Collect' && (!field.Value || field.Value.length === 0)) {
          field.ErrorMessage = "The value is required if deliveryTerms = Collect. Must be blank if deliveryTerms <> Collect.";
        }
      break;
      case CheckoutCommonFields.RequestedShipDate:
        if (!StringUtils.IsValidDateFormat(field.Value)) {
          field.ErrorMessage = "The date format is not valid. Must be in MM/DD/YYYY format.";
        }
        break;
      default:
        field.ErrorMessage = "";
        break;
    }
  }

  return checkoutCommonFields;
}

export const addMissingCheckoutCommonFields = (checkoutCommonFields: CheckoutCommonField[]): CheckoutCommonField[] => {
  requiredCheckoutCommonFieldNames.forEach(fieldName => {
    addMissingCheckoutCommonField(checkoutCommonFields, fieldName);
  });

  return checkoutCommonFields;
}

export const addMissingCheckoutCommonField = (checkoutCommonFields: CheckoutCommonField[], fieldName: string): CheckoutCommonField[] => {
  const fieldExists = checkoutCommonFields.find(x => x.FieldName === fieldName);
  if (!fieldExists) {
    checkoutCommonFields.push({
      FieldName: fieldName,
      Value: "",
    });
  }

  return checkoutCommonFields;
}

const isValidRequiredField = (field: CheckoutCommonField): boolean => {
  if (field.FieldName !== CheckoutCommonFields.MarkedPackages
    && field.FieldName !== CheckoutCommonFields.CollectAccountNumber
    && field.FieldName !== CheckoutCommonFields.CollectCarrier
    && field.FieldName !== CheckoutCommonFields.RequestedShipDate
    && field.FieldName !== CheckoutCommonFields.ShippingNotes
  ) {
      return field.Value && field.Value.length > 0;
  }

  return true;
}

const isValidMaxLengthValue = (field: CheckoutCommonField): { isValid: boolean; maxLen: number } => {
  // Treat null/undefined as empty for max-length validation; required checks are handled separately.
  const length = field.Value ? field.Value.length : 0;

  switch (field.FieldName) {
    case CheckoutCommonFields.PoInfoPoNumber:
      return { isValid: length <= 50, maxLen: 50 };
    case CheckoutCommonFields.BuyersEmail:
      return { isValid: length <= 100, maxLen: 100 };        
    case CheckoutCommonFields.ShipToCompanyName:
      return { isValid: length <= 100, maxLen: 100 };
    case CheckoutCommonFields.ShipToStreet:
      return { isValid: length <= 70, maxLen: 70 };
    case CheckoutCommonFields.ShipToCity:
      return { isValid: length <= 35, maxLen: 35 };
    case CheckoutCommonFields.ShippingNotes:
      return { isValid: length <= 255, maxLen: 255 };
    case CheckoutCommonFields.ShipToStateOrProvince:
      return { isValid: length <= 2, maxLen: 2 };
    case CheckoutCommonFields.ShipToZipOrPostal:
      return { isValid: length <= 10, maxLen: 10 };
    case CheckoutCommonFields.ShipToCountry:
      return { isValid: length <= 2, maxLen: 2 };
    case CheckoutCommonFields.BillToCompanyName:
      return { isValid: length <= 100, maxLen: 100 };
    case CheckoutCommonFields.BillToStreet:
      return { isValid: length <= 70, maxLen: 70 };
    case CheckoutCommonFields.BillToCity:
      return { isValid: length <= 35, maxLen: 35 };
    case CheckoutCommonFields.BillToStateOrProvince:
      return { isValid: length <= 2, maxLen: 2 };
    case CheckoutCommonFields.BillToZipOrPostal:
      return { isValid: length <= 10, maxLen: 10 };
    case CheckoutCommonFields.BillToCountry:
      return { isValid: length <= 2, maxLen: 2 };
    case CheckoutCommonFields.DeliveryTerms:
      return { isValid: length <= 50, maxLen: 50 };
    case CheckoutCommonFields.ShipVia:
      return { isValid: length <= 50, maxLen: 50 };
    case CheckoutCommonFields.CollectAccountNumber:
      return { isValid: length <= 30, maxLen: 30 };
    case CheckoutCommonFields.CollectCarrier:
      return { isValid: length <= 50, maxLen: 50 };
    case CheckoutCommonFields.MarkedPackages:
      return { isValid: length <= 255, maxLen: 255 };
    case CheckoutCommonFields.RequestedShipDate:
      return { isValid: length <= 10, maxLen: 10 };
    default:
      return { isValid: true, maxLen: 0 };
  }
}


